// vg-binding: P7.D-102
// vg-binding: P7.D-112
// vg-binding: P7.D-114
//
// apps/vendor/src/features/onboarding/api.ts
//
// Phase 7 Wave 4 Task 10 — TanStack-Query hooks for the onboarding wizard.
//
// Wraps three BE endpoints that landed in Wave 3:
//   - POST /api/v1/vendor/onboarding/submit   → useSubmitOnboarding
//   - POST /api/v1/vendor/kyc/documents       → useUploadKycDocument
//   - DELETE /api/v1/vendor/kyc/documents/:id → useDeleteKycDocument
//
// + the Wave 4 status endpoint:
//   - GET /api/v1/vendor/onboarding/status    → useOnboardingStatus
//
// All requests use cookie auth (`credentials: 'include'`) since the
// vendor SPA never sees the JWT directly.

import { csrfFetch } from '../../lib/api/csrfFetch';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Shared envelope types (mirrors INTERFACE-STANDARDS shape)
// ---------------------------------------------------------------------------

interface ApiSuccess<T> {
  ok: true;
  data: T;
  requestId?: string;
}

interface ApiError {
  ok: false;
  error: {
    code: string;
    message: string;
    user_message?: string;
    field_errors?: Record<string, string>;
  };
  requestId?: string;
}

export type ApiEnvelope<T> = ApiSuccess<T> | ApiError;

export class OnboardingApiError extends Error {
  public readonly code: string;
  public readonly statusCode: number;
  public readonly user_message: string | undefined;
  public readonly field_errors: Record<string, string> | undefined;

  constructor(
    code: string,
    message: string,
    statusCode: number,
    user_message?: string,
    field_errors?: Record<string, string>,
  ) {
    super(message);
    this.name = 'OnboardingApiError';
    this.code = code;
    this.statusCode = statusCode;
    this.user_message = user_message;
    this.field_errors = field_errors;
  }
}

async function unwrap<T>(res: Response): Promise<T> {
  const body = (await res.json().catch(() => null)) as ApiEnvelope<T> | null;
  if (!body) {
    throw new OnboardingApiError(
      'NETWORK_ERROR',
      'Empty response body',
      res.status,
    );
  }
  if (!('ok' in body) || body.ok !== true) {
    const err = (body as ApiError).error;
    throw new OnboardingApiError(
      err?.code ?? 'UNKNOWN',
      err?.message ?? 'Request failed',
      res.status,
      err?.user_message,
      err?.field_errors,
    );
  }
  return body.data;
}

// ---------------------------------------------------------------------------
// Request payload + response shapes — mirror the BE Zod contracts
// ---------------------------------------------------------------------------

export interface SubmitOnboardingPayload {
  business_info: {
    business_name: string;
    legal_name: string;
    contact_email: string;
    contact_phone: string;
    country: string;
    timezone: string;
    language: 'en' | 'vi';
  };
  kyc_doc_ids: string[];
  bank_account: {
    method: 'bank' | 'payoneer' | 'wise';
    holder_name: string;
    account_number: string;
    bank_name?: string;
    bank_country?: string;
    platform_account_id?: string;
  };
  warehouses: Array<{
    name: string;
    address: string;
    country: string;
    priority: number;
    is_active: boolean;
  }>;
  review_confirmed: true;
}

export interface SubmitOnboardingResult {
  status: 'pending';
  eta_business_days: number;
  vendor_id: string;
}

/**
 * A KYC document already on file. dbg-fixbug (2026-08-26): added so the wizard
 * can show what was uploaded after a page refresh — previously the upload state
 * lived only in component state and vanished on F5.
 */
export interface OnboardingKycDoc {
  id: string;
  /** Admin-side type: business_license | id_card | tax_cert | bank_proof. */
  doc_type: string;
  /** Absolute URL — openable directly. */
  file_url: string;
  original_name: string;
  status: 'pending' | 'approved' | 'rejected';
  reject_reason: string | null;
  uploaded_at: string;
}

export interface OnboardingStatus {
  step_completed: 0 | 1 | 2 | 3 | 4 | 5;
  kyc_status: 'pending' | 'approved' | 'rejected';
  payout_blocked: boolean;
  reject_reason: string | null;
  eta_business_days: number;
  /** Present since dbg-fixbug; older servers omit it. */
  kyc_documents?: OnboardingKycDoc[];
}

export interface KycUploadResult {
  doc_id: string;
  doc_type: 'business_license' | 'id_card' | 'tax_certificate' | 'bank_proof';
  file_url: string;
  uploaded_at: number;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

const ONBOARDING_STATUS_KEY = ['vendor', 'onboarding', 'status'] as const;

/**
 * GET /api/v1/vendor/onboarding/status — wizard resume + admin verdict poll.
 * Refetch interval = 30s when the wizard sits on the under-review page.
 */
export function useOnboardingStatus(opts?: {
  pollingMs?: number;
}): UseQueryResult<OnboardingStatus, OnboardingApiError> {
  return useQuery<OnboardingStatus, OnboardingApiError>({
    queryKey: ONBOARDING_STATUS_KEY,
    queryFn: async () => {
      const res = await csrfFetch('/api/v1/vendor/onboarding/status', {
        method: 'GET',
        credentials: 'include',
      });
      return unwrap<OnboardingStatus>(res);
    },
    refetchInterval: opts?.pollingMs ?? false,
    retry: 1,
  });
}

/**
 * POST /api/v1/vendor/onboarding/submit. Accepts a callback that yields the
 * `Idempotency-Key` so retries reuse the same key — the wizard store's
 * `getOrCreateIdempotencyKey` is the canonical source.
 */
export function useSubmitOnboarding(
  getIdempotencyKey: () => string,
): UseMutationResult<
  SubmitOnboardingResult,
  OnboardingApiError,
  SubmitOnboardingPayload
> {
  const qc = useQueryClient();
  return useMutation<SubmitOnboardingResult, OnboardingApiError, SubmitOnboardingPayload>({
    mutationFn: async (payload) => {
      const res = await csrfFetch('/api/v1/vendor/onboarding/submit', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': getIdempotencyKey(),
        },
        body: JSON.stringify(payload),
      });
      return unwrap<SubmitOnboardingResult>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ONBOARDING_STATUS_KEY });
    },
  });
}

export interface KycUploadInput {
  file: File;
  doc_type: 'business_license' | 'id_card' | 'tax_certificate' | 'bank_proof';
}

/**
 * POST /api/v1/vendor/kyc/documents — multipart upload one doc.
 */
export function useUploadKycDocument(): UseMutationResult<
  KycUploadResult,
  OnboardingApiError,
  KycUploadInput
> {
  return useMutation<KycUploadResult, OnboardingApiError, KycUploadInput>({
    mutationFn: async ({ file, doc_type }) => {
      const fd = new FormData();
      // doc_type MUST precede the file part: @fastify/multipart only exposes
      // form fields parsed BEFORE the file on `mp.fields`, and the route reads
      // doc_type before draining the body. With the file first, a large upload
      // streams past the field → doc_type undefined → 400 INVALID_DOC_TYPE
      // (small files slipped through because they were fully buffered). dbg.
      fd.append('doc_type', doc_type);
      fd.append('file', file);
      const res = await csrfFetch('/api/v1/vendor/kyc/documents', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      return unwrap<KycUploadResult>(res);
    },
  });
}

/**
 * DELETE /api/v1/vendor/kyc/documents/:id
 */
export function useDeleteKycDocument(): UseMutationResult<
  { deleted: true; doc_id: string },
  OnboardingApiError,
  string
> {
  return useMutation<{ deleted: true; doc_id: string }, OnboardingApiError, string>({
    mutationFn: async (docId) => {
      const res = await csrfFetch(`/api/v1/vendor/kyc/documents/${docId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      return unwrap<{ deleted: true; doc_id: string }>(res);
    },
  });
}
